/**
 * Modal para adicionar interesses (documentos de banco de dados) a um lead
 * Chrome = Modal shared (F72: portal, bottom-sheet mobile, always-light);
 * seleção tipo "carrinho"
 */

import React, { useState, useMemo } from 'react';
import { Modal, ModalHeader, Input, Select, Button } from '@/shared/components/ui';
import { useDatabases } from '@/modules/databases/hooks/useDatabases';
import { useDatabaseDocuments } from '@/modules/databases/hooks/useDatabaseDocuments';
import { PropertyCard } from '@/modules/databases/components/content/PropertyCard';
import { GenericDocumentCard } from '@/modules/databases/components/content/GenericDocumentCard';
import { PropertiesAdvancedFiltersComponent } from '@/modules/databases/components/content/PropertiesAdvancedFilters';
import type { PropertiesFilters } from '@/modules/databases/components/content/PropertiesAdvancedFilters';
import { ListPageLoading, ListPagePagination } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
export interface AddInterestModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  existingInterestIds: string[];
  onAdd: (documentIds: string[]) => Promise<void>;
}

export const AddInterestModal: React.FC<AddInterestModalProps> = ({
  isOpen,
  onClose,
  existingInterestIds,
  onAdd
}) => {
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<PropertiesFilters>({});

  // Fetch databases for dropdown
  const { data: databasesData } = useDatabases();

  const databases = databasesData?.items || [];

  const selectedDatabaseType = useMemo(() => {
    const db = databases.find((d: any) => d.id === selectedDatabaseId);
    return db?.type || 'properties';
  }, [databases, selectedDatabaseId]);

  // Build filters for documents query
  const documentFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    if (searchTerm) filters.search = searchTerm;

    // Add advanced filters for properties
    if (selectedDatabaseType === 'properties') {
      if (advancedFilters.opportunityType?.length) filters.opportunityType = advancedFilters.opportunityType;
      if (advancedFilters.propertyType?.length) filters.propertyType = advancedFilters.propertyType;
      if (advancedFilters.itemStatus?.length) filters.itemStatus = advancedFilters.itemStatus;
      if (advancedFilters.minBedrooms != null) filters.minBedrooms = advancedFilters.minBedrooms;
      if (advancedFilters.maxBedrooms != null) filters.maxBedrooms = advancedFilters.maxBedrooms;
      if (advancedFilters.minBathrooms != null) filters.minBathrooms = advancedFilters.minBathrooms;
      if (advancedFilters.maxBathrooms != null) filters.maxBathrooms = advancedFilters.maxBathrooms;
      if (advancedFilters.minParkingSpaces != null) filters.minParkingSpaces = advancedFilters.minParkingSpaces;
      if (advancedFilters.maxParkingSpaces != null) filters.maxParkingSpaces = advancedFilters.maxParkingSpaces;
      if (advancedFilters.minPrice != null) filters.minPrice = advancedFilters.minPrice;
      if (advancedFilters.maxPrice != null) filters.maxPrice = advancedFilters.maxPrice;
      if (advancedFilters.minArea != null) filters.minArea = advancedFilters.minArea;
      if (advancedFilters.maxArea != null) filters.maxArea = advancedFilters.maxArea;
      if (advancedFilters.city) filters.city = advancedFilters.city;
      if (advancedFilters.neighborhood) filters.neighborhood = advancedFilters.neighborhood;
    }

    return filters;
  }, [searchTerm, selectedDatabaseType, advancedFilters]);

  // Fetch documents from selected database
  const { data: documentsData, isLoading: documentsLoading } = useDatabaseDocuments({
    databaseId: selectedDatabaseId || '',
    filters: documentFilters,
    page: currentPage,
    limit: 12,
    enabled: !!selectedDatabaseId,
  });

  const documents = documentsData?.items || [];

  // Auto-select first database if none selected
  React.useEffect(() => {
    if (databases.length > 0 && !selectedDatabaseId) {
      setSelectedDatabaseId(databases[0].id);
    }
  }, [databases, selectedDatabaseId]);

  const toggleDocument = (docId: string) => {
    // Don't allow selecting already-added documents
    if (existingInterestIds.includes(docId)) return;

    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const handleDatabaseChange = (dbId: string) => {
    setSelectedDatabaseId(dbId);
    setCurrentPage(1);
    setSearchTerm('');
    setAdvancedFilters({});
  };

  const handleFiltersChange = (filters: PropertiesFilters) => {
    setAdvancedFilters(filters);
    setCurrentPage(1);
  };

  const handleAdd = async () => {
    if (selectedDocIds.size === 0) return;
    setIsAdding(true);
    try {
      await onAdd(Array.from(selectedDocIds));
    } finally {
      setIsAdding(false);
    }
  };

  // Check if there are active advanced filters
  const hasActiveFilters = useMemo(() => {
    return Object.values(advancedFilters).some(v => {
      if (Array.isArray(v)) return v.length > 0;
      return v != null && v !== '';
    });
  }, [advancedFilters]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="5xl">
      <div className="flex flex-col" data-testid="add-interest-modal">
        {/* Header — ModalHeader shared (título 18px como prop, F74) */}
        <ModalHeader title="Adicionar Interesses" onClose={onClose} />

        {/* Toolbar */}
        <div
          className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0"
          style={{ borderColor: '#e5e7eb' }}
        >
          {/* Database Dropdown */}
          <Select
            value={selectedDatabaseId || ''}
            onChange={(val) => handleDatabaseChange(val)}
            options={databases.map((db: any) => ({
              value: db.id,
              label: db.name
            }))}
            fullWidth={false}
            style={{ minWidth: '200px' }}
          />

          {/* Search */}
          <div className="flex-1 max-w-md">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Buscar documentos..."
              icon={'search'}
            />
          </div>
        </div>

        {/* Filter Chips Bar (only for properties) */}
        {selectedDatabaseType === 'properties' && (
          <div
            className="px-4 py-2 flex items-center gap-3 border-b flex-shrink-0"
            style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
          >
            <div
              className="flex items-center gap-1.5 flex-shrink-0"
              style={{ color: '#64748b' }}
            >
              <Icon name="filter" className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">Filtros</span>
              {hasActiveFilters && (
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: '#8b5cf6' }}
                />
              )}
            </div>
            <div className="w-px h-5 flex-shrink-0" style={{ backgroundColor: '#e5e7eb' }} />
            <PropertiesAdvancedFiltersComponent
              filters={advancedFilters}
              onFiltersChange={handleFiltersChange}
            />
          </div>
        )}

        {/* Content - Scrollable (scroll interno mantém header/toolbar/footer fixos) */}
        <div className="flex-1 overflow-y-auto p-4 max-h-[55vh]">
          {documentsLoading && <ListPageLoading message="Carregando documentos..." />}

          {!documentsLoading && documents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <Icon name="database" className="w-10 h-10 mb-3" style={{ color: '#d1d5db' }} />
              <p className="text-sm font-medium" style={{ color: '#64748b' }}>
                Nenhum documento encontrado
              </p>
              <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                {searchTerm ? 'Tente buscar com outros termos' : 'Selecione um banco de dados'}
              </p>
            </div>
          )}

          {!documentsLoading && documents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map((doc: any) => {
                const isSelected = selectedDocIds.has(doc.id);
                const isAlreadyAdded = existingInterestIds.includes(doc.id);

                return (
                  <div
                    key={doc.id}
                    className="relative cursor-pointer"
                    onClick={() => toggleDocument(doc.id)}
                    style={{
                      opacity: isAlreadyAdded ? 0.5 : 1,
                      pointerEvents: isAlreadyAdded ? 'none' : 'auto'
                    }}
                  >
                    {/* Selection border only (no checkbox) */}
                    <div
                      className="rounded-lg transition-all"
                      style={{
                        border: isSelected ? '2px solid #8b5cf6' : '2px solid transparent',
                        borderRadius: '0.5rem'
                      }}
                    >
                      {selectedDatabaseType === 'properties' ? (
                        <PropertyCard property={doc} alwaysLight />
                      ) : (
                        <GenericDocumentCard document={doc} alwaysLight />
                      )}
                    </div>

                    {/* Already added badge */}
                    {isAlreadyAdded && (
                      <div
                        className="absolute bottom-4 left-4 z-10 px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: '#FEF3C7',
                          color: '#92400E'
                        }}
                      >
                        Já adicionado
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with pagination integrated (spec do ModalFooter: px-4 py-3, bg #f8fafc) */}
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 border-t flex-shrink-0"
          style={{
            backgroundColor: '#f8fafc',
            borderColor: '#e5e7eb',
            borderBottomLeftRadius: 'var(--radius-2xl)',
            borderBottomRightRadius: 'var(--radius-2xl)',
          }}
        >
          <span className="text-sm flex-shrink-0" style={{ color: '#64748b' }}>
            {selectedDocIds.size > 0
              ? `${selectedDocIds.size} selecionado${selectedDocIds.size > 1 ? 's' : ''}`
              : 'Nenhum documento selecionado'}
          </span>

          {/* Pagination centered */}
          {!documentsLoading && documentsData && documentsData.totalPages > 1 && (
            <div className="flex-1 flex justify-center">
              <ListPagePagination
                currentPage={documentsData.page}
                totalPages={documentsData.totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              variant="secondary"
              onClick={onClose}
              data-testid="add-interest-cancel"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAdd}
              disabled={selectedDocIds.size === 0 || isAdding}
              data-testid="add-interest-confirm"
            >
              {isAdding ? 'Adicionando...' : `Adicionar interesse${selectedDocIds.size > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
